import {useEffect, useState} from 'react';
import {useTranslationWithKeys} from '../../../hooks/useTranslationWithKeys';
import {AlertCircle, Apple, CheckCircle2, Download, Laptop, Loader2, Monitor, Smartphone} from 'lucide-react';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '../../basic/card';
import {Button} from '../../basic/button';
import {Badge} from '../../basic/badge';
import {Alert, AlertDescription, AlertTitle} from '../../basic/alert';
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from '../../basic/accordion';
import {useGetWebsiteContentQuery} from '../../../store/generated/api';
import {config} from '../../../config';
import {getVelasolarisUrl} from '../../../utils/websiteUrlUtils';
import {DownloadCard, parseDownloadCards, parseReleaseNotes, ReleaseNote, Requirement} from '../../../utils/htmlParser';
import {isMobileDevice} from '../../../utils/deviceUtils';

const DOWNLOAD_URL_BY_PLATFORM: Record<DownloadCard['platform'], string | undefined> = {
  windows: config.downloadUrlWindows,
  mac: config.downloadUrlMac,
  linux: config.downloadUrlLinux
};

export function DownloadView() {
    const {i18n, t} = useTranslationWithKeys();
  const language = i18n.language.startsWith('de') ? 'de' : 'en';

    const [downloadCards, setDownloadCards] = useState<DownloadCard[]>([]);
    const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const downloadsUrl = getVelasolarisUrl('downloads', language);
  const releaseNotesUrl = getVelasolarisUrl('release-notes', language);

  const {
    data: downloadsHtml,
    isLoading: downloadsLoading,
    error: downloadsError
  } = useGetWebsiteContentQuery({url: downloadsUrl});
  const {
    data: releaseNotesHtml,
    isLoading: releaseNotesLoading,
    error: releaseNotesError
  } = useGetWebsiteContentQuery({url: releaseNotesUrl});

  useEffect(() => {
    if (!downloadsLoading && !releaseNotesLoading) {
      setLoading(false);
    }

    if (downloadsHtml) {
      try {
        const parsed = parseDownloadCards(downloadsHtml)
            .map(card => ({
              ...card,
              downloadUrl: DOWNLOAD_URL_BY_PLATFORM[card.platform] || ''
            }));
        setDownloadCards(parsed);
      } catch {
        setError('Failed to parse download cards');
      }
    }

    if (releaseNotesHtml) {
      try {
        const parsed = parseReleaseNotes(releaseNotesHtml);
        setReleaseNotes(parsed);
      } catch {
        setError('Failed to parse release notes');
      }
    }

    if (downloadsError || releaseNotesError) {
      setError('Failed to fetch data from website');
    }
  }, [downloadsHtml, downloadsLoading, downloadsError, releaseNotesHtml, releaseNotesLoading, releaseNotesError]);

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'windows':
        return <Monitor className="h-6 w-6"/>;
      case 'mac':
        return <Apple className="h-6 w-6"/>;
      case 'linux':
        return <Laptop className="h-6 w-6"/>;
      default:
        return <Monitor className="h-6 w-6"/>;
    }
  };

  const getPlatformName = (platform: string) => {
    switch (platform) {
      case 'windows':
        return 'Windows';
      case 'mac':
        return 'macOS';
      case 'linux':
        return 'Linux';
      default:
        return platform;
    }
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary"/>
        </div>
    );
  }

  if (error) {
    return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4"/>
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
    );
  }

  if (isMobileDevice()) {
    return (
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('downloads.title')}</h1>
            <p className="text-muted-foreground mt-2">{t('downloads.description')}</p>
          </div>

          <Alert>
            <Smartphone className="h-4 w-4"/>
            <AlertTitle>{t('downloads.mobileMessageTitle')}</AlertTitle>
            <AlertDescription>{t('downloads.mobileMessage')}</AlertDescription>
          </Alert>
        </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('downloads.title')}</h1>
        <p className="text-muted-foreground mt-2">{t('downloads.description')}</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {downloadCards.map((card, index) => (
            <Card key={index} className="flex flex-col">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getPlatformIcon(card.platform)}
                    <CardTitle className="text-xl">{getPlatformName(card.platform)}</CardTitle>
                  </div>
                  <Badge variant="secondary">{card.version}</Badge>
                </div>
                <CardDescription>{card.title}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between">
                <div className="space-y-2 mb-4">
                  <div className="text-sm font-medium">{t('downloads.requirements')}</div>
                  <ul className="space-y-1">
                      {card.requirements.map((req: Requirement, reqIndex: number) => (
                        <li key={reqIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0"/>
                          <span>{req.label}</span>
                        </li>
                    ))}
                  </ul>
                </div>
                {card.downloadUrl ? (
                  <Button asChild className="w-full">
                    <a href={card.downloadUrl} target="_blank" rel="noopener noreferrer">
                      <Download className="mr-2 h-4 w-4"/>
                      {t('downloads.download')}
                    </a>
                  </Button>
                ) : (
                  <Button className="w-full" disabled>
                    <Download className="mr-2 h-4 w-4"/>
                    {t('downloads.download')}
                  </Button>
                )}
              </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="text-2xl font-bold tracking-tight mb-4">{t('downloads.releaseNotes')}</h2>
        {releaseNotes.length > 0 ? (
            <Accordion type="single" collapsible className="w-full">
              {releaseNotes.map((note, index) => (
                  <AccordionItem key={index} value={`version-${index}`}>
                    <AccordionTrigger>
                      <div className="flex items-center gap-3 text-left">
                        <Badge variant="outline">{note.version}</Badge>
                        <span className="text-sm text-muted-foreground">{note.date}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 mt-4">
                        {note.items.map((item: string, itemIndex: number) => (
                            <li key={itemIndex} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary"/>
                              <span>{item}</span>
                            </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
              ))}
            </Accordion>
        ) : (
            <Alert>
              <AlertCircle className="h-4 w-4"/>
              <AlertTitle>{t('downloads.noReleaseNotes')}</AlertTitle>
              <AlertDescription>{t('downloads.noReleaseNotesDescription')}</AlertDescription>
            </Alert>
        )}
      </div>
    </div>
  );
}
